/**
 * **运行通道**（`runGraph`）—— "点一下开始，把这条链跑一遍"。
 *
 * ## 它和设计期求值的关系（这是本文件最重要的一条）
 *
 * 两条通道**共用同一个求值引擎**，只是"副作用节点"多了一个异步执行器：
 *
 * ```
 *   设计期  evaluateGraph(models, edges, { evaluators })
 *             · 纯节点：真算
 *             · 副作用节点：产出「未运行」占位（**绝不**在设计期偷偷跑一次）
 *
 *   运行期  runGraph(models, edges, { evaluators, runners, targets, signal })
 *             · 先按拓扑序 await 每个副作用节点的 runner
 *             · 再把结果当**预置值**（seeds）交给同一个 evaluateGraph 算纯节点
 * ```
 *
 * ★ 所以 `NodeRunner` 与 `NodeEvaluator` **同形**，只差 async：
 *
 * ```ts
 * type NodeEvaluator = (api: NodeEvalApi) => Record<string, unknown>
 * type NodeRunner    = (api: NodeEvalApi) => Promise<Record<string, unknown>>
 * ```
 *
 * 好处是**输入怎么取只写了一份**：副作用节点拿到的 `api` 与纯节点完全一样
 * （`input()` 沿边回溯、`repeat()` 展开变参行、`literal()` 读内联值）。
 * 各写一套的话，"同一条线在预览里读到 A、在运行里读到 B"这类 bug 迟早出现，
 * 而且极难查。
 *
 * ## 运行范围 = **触发节点的下游闭包**
 *
 * `targets` 给的是"这次是谁触发的"（事件节点）—— 只有它的下游该跑。
 * 不给就是整张图。**别的分支不跑**：那边可能有它自己的触发器和副作用，
 * 顺手跑掉等于替用户做了他没说要做的事。
 *
 * ## ⚠️ v1 的已知边界（写清楚，别让人以为坏了）
 *
 * * **组 / 循环内层不跑副作用**：`runSubgraph` 走的是纯求值器。
 *   把副作用放进组里目前会得到"未运行"占位（不是静默跑一半）。
 * * **不做重试**：失败就是失败，`RunResult` 里带具体节点与信息；
 *   重试策略属于产品层（打印那种副作用尤其不能自动重试 —— 会重复出纸）。
 */
import {
  evaluateGraph,
  type Diagnostic,
  type GraphEdge,
  type NodeEvalApi,
  type NodeEvaluator,
} from './evaluate'
import type { GraphNodeModel } from '../types/node'

/** 执行器拿到的运行上下文 */
export interface RunContext {
  /**
   * **取消信号**：长任务（抓网页 / 等设备）必须把它交给底层调用
   * （`fetch(url, { signal })` 这种），否则用户点了取消还得等它跑完 ——
   * 那等于没有取消。
   */
  signal: AbortSignal
  /** 这次是谁触发的（日志 / 诊断里写进去） */
  triggeredBy: readonly string[]
}

/**
 * **副作用节点的执行器**：与 `NodeEvaluator` 同形，只是可以异步，
 * 而且多一个运行上下文。
 *
 * ⚠️ 为什么上下文是**第二个参数**而不是塞进 `api`：`api` 是"读图"的接口，
 * 设计期与运行期**共用同一份**（这正是"两条通道不分叉"的保证）——
 * 往里塞运行期才有的东西（信号 / 触发器），那份共用就不成立了。
 */
export type NodeRunner = (
  api: NodeEvalApi,
  context: RunContext,
) => Promise<Record<string, unknown>>

/** 节点在这次运行里的状态 */
export type RunNodeStatus =
  /** 纯节点：算过了 */
  | 'computed'
  /** 副作用节点：跑完了 */
  | 'ran'
  /** 上游失败/被取消 → 跳过 */
  | 'skipped'
  /** 自己失败了 */
  | 'failed'
  /** 排队时被取消（还没轮到） */
  | 'cancelled'

export interface RunNodeResult {
  nodeId: string
  type: string
  /** 纯节点还是副作用节点 */
  kind: 'pure' | 'effect'
  status: RunNodeStatus
  /** 这一步花了多久（毫秒）—— 抓网页慢在哪一步，看它 */
  durationMs: number
  /** 失败信息（只有 `failed` 有） */
  error?: string
  /** 这次运行里它读到的输入（按 handle）—— 运行历史要能回答"当时读到的是什么" */
  inputs?: Record<string, unknown>
  /** 它产出的输出（同上） */
  outputs?: Record<string, unknown>
}

/** 这次运行的**结果**：没有下游的那些节点产出的值（工具箱里"拿到结果"就是它） */
export interface RunSink {
  nodeId: string
  /** 节点的显示名（模型上的 label / 标题）—— 界面上直接显示，别让用户去认 id */
  label: string
  type: string
  handle: string
  value: unknown
}

export interface RunResult {
  /** 这次跑了哪些节点（顺序 = 实际执行顺序） */
  nodes: RunNodeResult[]
  /** 整图的输出（`nodeId → handle → 值`）—— 与设计期同一个形状 */
  outputs: Map<string, Map<string, unknown>>
  diagnostics: Diagnostic[]
  startedAt: number
  finishedAt: number
  /** 是不是被取消的（用户点了取消 / 超时） */
  cancelled: boolean
  /** 触发它的节点（`targets` 里的那些） */
  triggeredBy: string[]
  /**
   * **结果**（终点节点的输出）。
   *
   * ⚠️ 为什么要在运行期算出来：判断"谁是终点"要用**边**，而输出面板
   * 拿不到图（它是内核的一个组件）。在这里算好塞进结果里，
   * 面板就只管显示。
   */
  sinks: RunSink[]
}

/**
 * **一次运行的记录**（运行历史 / 结果视图读它）。
 *
 * ⚠️ 它定义在**内核**而不是宿主：形状属于"运行这件事"本身，
 * 而 `NpLogPanel`（内核组件）要按它渲染 —— 放在宿主层的话，
 * 内核组件就得反向 import 宿主，分层守卫会（也确实）当场报错。
 */
export interface RunRecord {
  /** 第几次（从 1 开始，界面上显示"第 3 次"） */
  seq: number
  startedAt: number
  finishedAt: number
  durationMs: number
  /** 谁触发的（事件节点的 id 列表） */
  by: readonly string[]
  cancelled: boolean
  /** 失败的原因（拒绝执行 / 有节点失败）—— 成功时为空 */
  failure: string
  nodes: readonly RunNodeResult[]
  /** 终点的值：这次运行算出来的输出（`nodeId → handle → 值`） */
  outputs: ReadonlyMap<string, ReadonlyMap<string, unknown>>
  /** **结果**（终点节点的输出）—— 结果视图先显示它 */
  sinks: readonly RunSink[]
}

/**
 * 运行记录 → 一行摘要。
 *
 * ⚠️ **只有这一份**：按钮的 tooltip 与输出面板里那一行都调它。
 * 各写一份的话，"按钮说 2 个动作、面板说 3 个"这种事迟早出现。
 */
export function summarizeRun(record: RunRecord): string {
  if (record.failure) return `运行失败：${record.failure}`
  if (record.cancelled) return `已取消 · ${record.nodes.length} 个节点`
  const effects = record.nodes.filter((item) => item.kind === 'effect')
  const ran = effects.filter((item) => item.status === 'ran').length
  const ms = record.durationMs
  const time = ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
  return `${ran} 个动作 · ${record.nodes.length} 个节点 · ${time}`
}

export interface RunOptions {
  /** 纯节点求值器（**与设计期同一批**） */
  evaluators: Record<string, NodeEvaluator>
  /** 副作用节点的执行器（只有它们需要） */
  runners?: Record<string, NodeRunner>
  /** 数据源内容 */
  data?: Record<string, Record<string, unknown>>
  /** 触发节点：只有它们的**下游闭包**会跑。不给 = 整张图 */
  targets?: readonly string[]
  /** 取消信号 */
  signal?: AbortSignal
  /** 每跑完一个副作用节点回调一次（进度条 / 逐节点状态用） */
  onNode?: (result: RunNodeResult) => void
}

/* ==========================================================================
   图上的两个小工具（纯函数，可单测）
   ========================================================================== */

/** `nodeId → 它的下游节点集合`（沿边正向） */
function downstreamMap(edges: readonly GraphEdge[]): Map<string, Set<string>> {
  const next = new Map<string, Set<string>>()
  for (const edge of edges) {
    if (edge.muted) continue
    let set = next.get(edge.source)
    if (!set) {
      set = new Set()
      next.set(edge.source, set)
    }
    set.add(edge.target)
  }
  return next
}

/**
 * **下游闭包**：从这些节点出发，所有会被它们影响到的节点（含自己）。
 *
 * ⚠️ 遇到环要能停（图里理论上不成环，但运行通道不该假设这一点 ——
 * 求值层遇到环会报诊断，这里静默收敛即可，别卡死）。
 */
export function downstreamClosure(
  edges: readonly GraphEdge[],
  targets: readonly string[],
  allNodeIds?: readonly string[],
): Set<string> {
  const next = downstreamMap(edges)
  const seen = new Set<string>()
  const queue = [...targets]
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (seen.has(id)) continue
    seen.add(id)
    for (const item of next.get(id) ?? []) {
      if (!seen.has(item)) queue.push(item)
    }
  }
  if (allNodeIds) {
    /* 触发节点本身可能不在 allNodeIds 里（比如它属于别的层）—— 保留它 */
    for (const id of seen) {
      if (!allNodeIds.includes(id)) seen.delete(id)
    }
  }
  return seen
}

/**
 * 这些节点的**拓扑序**（只算给定的子集，忽略子集外的边）。
 *
 * 副作用必须按依赖顺序跑：上游没跑完，下游读到的就是"未运行"。
 * ⚠️ 环里的节点排不进去 —— 它们由调用方按"跳过"处理（求值层会对环报诊断）。
 */
export function topologicalOrder(
  models: readonly GraphNodeModel[],
  edges: readonly GraphEdge[],
  subset?: ReadonlySet<string>,
): string[] {
  const ids = models.map((m) => m.id).filter((id) => !subset || subset.has(id))
  const inSubset = new Set(ids)
  const inDegree = new Map<string, number>(ids.map((id) => [id, 0]))
  const next = new Map<string, string[]>()
  for (const edge of edges) {
    if (edge.muted) continue
    if (!inSubset.has(edge.source) || !inSubset.has(edge.target)) continue
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1)
    const list = next.get(edge.source) ?? []
    list.push(edge.target)
    next.set(edge.source, list)
  }
  const ready = ids.filter((id) => (inDegree.get(id) ?? 0) === 0)
  const order: string[] = []
  while (ready.length > 0) {
    const id = ready.shift() as string
    order.push(id)
    for (const target of next.get(id) ?? []) {
      const left = (inDegree.get(target) ?? 0) - 1
      inDegree.set(target, left)
      if (left === 0) ready.push(target)
    }
  }
  return order
}

/* ==========================================================================
   运行
   ========================================================================== */

/**
 * 跑一次。
 *
 * 步骤（也是断言的骨架）：
 *
 * ```
 *   ① 范围   = targets 的下游闭包（不给 targets 就是整张图）
 *   ② 副作用 = 按拓扑序，逐个 await runner；每个都拿"当前已知的 seeds"求值
 *   ③ 纯节点 = 全部副作用跑完后，用**同一个 evaluateGraph** 算一次（带 seeds）
 * ```
 *
 * ⚠️ 副作用**只跑一次**（不是"求值一次、运行又一次"）：第 ② 步里给副作用节点
 * 装的是**捕获型 evaluator**，它不产出值、只把 `api` 交出来 ——
 * 于是 runner 用的是与纯节点**同一个**输入读取接口，而副作用没有第二次执行。
 */
export async function runGraph(
  models: readonly GraphNodeModel[],
  edges: readonly GraphEdge[],
  options: RunOptions,
): Promise<RunResult> {
  const startedAt = Date.now()
  const runners = options.runners ?? {}
  const signal = options.signal
  const nodes: RunNodeResult[] = []

  const scope = options.targets
    ? downstreamClosure(
        edges,
        options.targets,
        models.map((m) => m.id),
      )
    : new Set(models.map((m) => m.id))

  /* 只把范围内的节点与边交给求值器：别的分支不该被算到（更不该被跑到） */
  const scopedModels = models.filter((model) => scope.has(model.id))
  const scopedEdges = edges.filter((edge) => scope.has(edge.source) && scope.has(edge.target))

  const seeds: Record<string, Record<string, unknown>> = {}
  const effectIds = scopedModels
    .filter((model) => Boolean(runners[model.type]))
    .map((model) => model.id)
  const effectSet = new Set(effectIds)
  const order = topologicalOrder(scopedModels, scopedEdges, effectSet)
  /*
   * ⚠️ 拓扑序里排不进去的（成环）也留个记录：不然它们会**静静地不跑**，
   * 而用户看到的是"点了没反应"。
   */
  const unordered = effectIds.filter((id) => !order.includes(id))

  let cancelled = false

  /**
   * 把"排不进执行顺序"的副作用节点**如实记下来**。
   *
   * ⚠️ 这条曾经只在"某个副作用失败"那条返回路径上写 —— 于是**全是环**
   * 的图一个节点记录都没有，用户看到的是"点了没反应"（测试当场抓到）。
   * 现在两条返回路径都要过它。
   */
  function recordUnordered(): void {
    for (const id of unordered) {
      if (nodes.some((item) => item.nodeId === id)) continue
      const model = scopedModels.find((item) => item.id === id)
      if (!model) continue
      const result: RunNodeResult = {
        nodeId: id,
        type: model.type,
        kind: 'effect',
        status: 'skipped',
        durationMs: 0,
        error: '依赖成环，没能排进执行顺序',
      }
      nodes.push(result)
      options.onNode?.(result)
    }
  }

  for (const nodeId of order) {
    if (signal?.aborted) {
      cancelled = true
      break
    }
    const model = scopedModels.find((item) => item.id === nodeId)
    if (!model) continue
    const runner = runners[model.type]
    if (!runner) continue

    /*
     * 捕获型 evaluator：求值这一轮时，这个节点的"求值"只是把 api 交出来。
     * 于是 runner 拿到的输入读取接口与纯节点**完全一致**（同一个 makeApi）。
     */
    let captured: NodeEvalApi | null = null
    evaluateGraph(scopedModels, scopedEdges, {
      data: options.data,
      seeds,
      evaluators: {
        ...options.evaluators,
        [model.type]: (api: NodeEvalApi) => {
          captured = api
          return {}
        },
      },
    })
    if (!captured) {
      /* 这个节点没被求值到（例如它在一个排不进的环里）→ 如实报，不要静默 */
      nodes.push({
        nodeId,
        type: model.type,
        kind: 'effect',
        status: 'skipped',
        durationMs: 0,
        error: '依赖成环，没能排进执行顺序',
      })
      continue
    }

    const api = captured as NodeEvalApi
    const inputs = collectInputs(model, api)
    const stepStart = Date.now()
    try {
      const outputs = await runner(api, {
        /* 每步一个新信号：`abort()` 由控制器的那个 controller 触发 */
        signal: signal ?? new AbortController().signal,
        triggeredBy: options.targets ?? [],
      })
      seeds[nodeId] = outputs ?? {}
      const result: RunNodeResult = {
        nodeId,
        type: model.type,
        kind: 'effect',
        status: 'ran',
        durationMs: Date.now() - stepStart,
        inputs,
        outputs: outputs ?? {},
      }
      nodes.push(result)
      options.onNode?.(result)
    } catch (error) {
      const result: RunNodeResult = {
        nodeId,
        type: model.type,
        kind: 'effect',
        status: 'failed',
        durationMs: Date.now() - stepStart,
        error: error instanceof Error ? error.message : String(error),
        inputs,
      }
      nodes.push(result)
      options.onNode?.(result)
      /*
       * ⚠️ 失败**不继续往下跑**：下游读到的会是"未运行"，
       * 继续跑只会产生一半对一半错的结果，比停下来更难查。
       * （重试 / 容错是产品层的策略，不在这里偷偷做 —— 打印那种副作用
       * 自动重试就是重复出纸。）
       */
      const remaining = order.slice(order.indexOf(nodeId) + 1)
      for (const id of remaining) {
        const rest = scopedModels.find((item) => item.id === id)
        if (rest) {
          nodes.push({
            nodeId: id,
            type: rest.type,
            kind: 'effect',
            status: 'skipped',
            durationMs: 0,
            error: '上游失败，已跳过',
          })
        }
      }
      recordUnordered()
      return {
        nodes,
        outputs: new Map(),
        diagnostics: [
          {
            nodeId,
            severity: 'error',
            message: `运行失败：${result.error}`,
          },
        ],
        startedAt,
        finishedAt: Date.now(),
        cancelled: false,
        triggeredBy: [...(options.targets ?? [])],
        /* 失败了就没有"结果"可言 —— 空数组比"半个结果"诚实 */
        sinks: [],
      }
    }
  }

  if (signal?.aborted && !cancelled) cancelled = true

  /* 成环的：如实记下来（不是静默不跑） */
  recordUnordered()

  /* 被取消：余下的副作用如实标"取消"，不再跑 */
  for (const nodeId of order) {
    if (nodes.some((item) => item.nodeId === nodeId)) continue
    const model = scopedModels.find((item) => item.id === nodeId)
    if (!model) continue
    const result: RunNodeResult = {
      nodeId,
      type: model.type,
      kind: 'effect',
      status: 'cancelled',
      durationMs: 0,
      error: cancelled ? '已取消' : undefined,
    }
    nodes.push(result)
    options.onNode?.(result)
  }

  /*
   * 纯节点：用**同一个** evaluateGraph 算一次（带副作用产出的 seeds）。
   * 这正是"运行与预览在纯的部分永远一致"的落点 —— 同一段代码、同一批求值器。
   */
  const final = cancelled
    ? { outputs: new Map<string, Map<string, unknown>>(), diagnostics: [] as Diagnostic[] }
    : evaluateGraph(scopedModels, scopedEdges, {
        data: options.data,
        seeds,
        evaluators: options.evaluators,
      })

  for (const model of scopedModels) {
    if (effectSet.has(model.id)) continue
    const produced = final.outputs.get(model.id)
    nodes.push({
      nodeId: model.id,
      type: model.type,
      kind: 'pure',
      status: cancelled ? 'skipped' : 'computed',
      durationMs: 0,
      outputs: produced ? Object.fromEntries(produced) : undefined,
    })
  }

  return {
    nodes,
    outputs: final.outputs,
    diagnostics: final.diagnostics,
    startedAt,
    finishedAt: Date.now(),
    cancelled,
    triggeredBy: [...(options.targets ?? [])],
    sinks: sinksOf(scopedModels, scopedEdges, final.outputs),
  }
}

/**
 * **终点**：范围内**没有出边**的节点（它们的输出就是这次运行的结果）。
 *
 * ⚠️ 副作用节点也参与（`tool.http` 的输出可能直接被看），
 * 而"没有值"的端口直接跳过 —— 否则结果区会出现一堆 `—`。
 */
function sinksOf(
  models: readonly GraphNodeModel[],
  edges: readonly GraphEdge[],
  outputs: ReadonlyMap<string, ReadonlyMap<string, unknown>>,
): RunSink[] {
  const hasOutgoing = new Set<string>()
  for (const edge of edges) {
    if (edge.muted) continue
    hasOutgoing.add(edge.source)
  }
  const sinks: RunSink[] = []
  for (const model of models) {
    if (hasOutgoing.has(model.id)) continue
    const produced = outputs.get(model.id)
    if (!produced) continue
    const label = model.label ?? model.type
    for (const [handle, value] of produced) {
      if (value === undefined) continue
      sinks.push({ nodeId: model.id, label, type: model.type, handle, value })
    }
  }
  return sinks
}

/** 把这次运行读到的输入抄一份（运行历史要能回答"当时读到的是什么"） */
function collectInputs(model: GraphNodeModel, api: NodeEvalApi): Record<string, unknown> {
  const inputs: Record<string, unknown> = {}
  for (const row of model.rows) {
    if (row.kind === 'input') {
      inputs[row.socket.id] = api.input(row.socket.id)
      continue
    }
    if (row.kind === 'repeater') {
      inputs[row.id] = api.repeat(row.id)
    }
  }
  return inputs
}

/* ==========================================================================
   串行控制器：一次只跑一个，多的排队
   ========================================================================== */

export interface RunRequest extends Omit<RunOptions, 'signal' | 'onNode'> {
  /** 要跑的图（控制器不持有图 —— 它只管"一次一个"这条策略） */
  models: readonly GraphNodeModel[]
  edges: readonly GraphEdge[]
  /** 这次是**谁**触发的（事件节点的 id）—— 显示成"由 XX 触发" */
  by?: string
  /** 状态变化回调（界面订阅它） */
  onNode?: (result: RunNodeResult) => void
  onState?: (state: RunControllerState) => void
}

export interface RunControllerState {
  /** 正在跑的那次（没有就是 null） */
  running: { by?: string; startedAt: number } | null
  /** 排队等着跑的（先进先出） */
  queued: number
  /** 最近一次结果（运行历史的第一条；完整历史留在宿主） */
  last: RunResult | null
}

/**
 * **串行运行控制器**：连点两下按钮不会并发跑两次。
 *
 * 为什么必须有它（而不是让调用方自觉）：一次运行可能带副作用（发请求、
 * 打印）—— 并发跑两次的后果不是"慢一点"，是**做了两遍**。
 * 所以策略写在这一层，默认**排队**（而不是丢弃：用户点两下通常是想跑两次）。
 */
export function createRunController(
  /**
   * 真正的运行函数（默认就是 `runGraph`）。
   *
   * ⚠️ 做成注入点是为了**测试能塞一个假的运行器**：控制器要验的是
   * "串行 / 排队 / 取消"这套策略，与图怎么算无关 —— 拿真图去验策略，
   * 断言会被图的行为搅浑。
   */
  run: (
    models: readonly GraphNodeModel[],
    edges: readonly GraphEdge[],
    options: RunOptions,
  ) => Promise<RunResult> = runGraph,
): {
  request: (request: RunRequest) => Promise<RunResult>
  cancel: () => void
  state: () => RunControllerState
} {
  let current: AbortController | null = null
  let running: { by?: string; startedAt: number } | null = null
  let last: RunResult | null = null
  const queue: { request: RunRequest; resolve: (result: RunResult) => void }[] = []
  let listeners: ((state: RunControllerState) => void)[] = []

  const snapshot = (): RunControllerState => ({ running, queued: queue.length, last })
  const notify = (): void => {
    for (const listener of listeners) listener(snapshot())
  }

  async function drain(): Promise<void> {
    if (running) return
    const next = queue.shift()
    if (!next) return
    const controller = new AbortController()
    current = controller
    running = { by: next.request.by, startedAt: Date.now() }
    next.request.onState?.(snapshot())
    notify()
    try {
      /* 直接调 `run`（注入点）—— 测试里可以塞一个假的运行器 */
      const { models, edges, ...options } = next.request
      last = await run(models, edges, { ...options, signal: controller.signal })
      next.resolve(last)
    } finally {
      running = null
      current = null
      listeners = listeners.filter((listener) => listener !== next.request.onState)
      next.request.onState?.(snapshot())
      notify()
      void drain()
    }
  }

  return {
    request(request: RunRequest): Promise<RunResult> {
      return new Promise<RunResult>((resolve) => {
        queue.push({ request, resolve })
        if (request.onState) listeners.push(request.onState)
        request.onState?.(snapshot())
        notify()
        void drain()
      })
    },
    cancel(): void {
      current?.abort()
    },
    state: snapshot,
  }
}
